<template>
    <div class="plan-drug-list">
        <div class="d-flex align-items-center justify-content-between">
            <h5 class="mb-0">
                {{ title }}
                <HelpPopup>
                    <template v-if="isBlocked">
                        <p>Drugs listed here <strong>cannot</strong> be billed to this plan when enforcement is active.</p>
                        <p>Enforcement is active only when <strong>both</strong> this plan's
                            <em>Enforce Blocked Drug List</em> setting is on <strong>and</strong> the tenant-wide
                            <em>Enforce Blocked Drug List</em> configuration is enabled. Otherwise this list has no effect.</p>
                        <p>Use the <strong>Effective</strong> and <strong>Expiration</strong> dates to schedule when a
                            drug becomes blocked or when the block automatically lifts. Effective is inclusive (the block
                            starts that day); Expiration is exclusive (the drug is billable again starting on that date).</p>
                    </template>
                    <template v-else>
                        <p>Drugs listed here are the <strong>only</strong> drugs that may be billed to this plan when
                            enforcement is active.</p>
                        <p>Enforcement is active only when <strong>both</strong> this plan's
                            <em>Enforce Approved Drug List</em> setting is on <strong>and</strong> the tenant-wide
                            <em>Enforce Approved Drug List</em> configuration is enabled. Otherwise this list has no effect.</p>
                        <p>Use the <strong>Effective</strong> and <strong>Expiration</strong> dates to schedule a drug to
                            become billable or to automatically fall off the list on a future date. Expiration is exclusive
                            — the drug is blocked starting on the expiration date.</p>
                    </template>
                </HelpPopup>
            </h5>
            <b-button size="sm" variant="outline-secondary" @click="openHistory">
                <b-icon icon="clock-history" /> History
            </b-button>
        </div>

        <b-alert v-if="enforced" show :variant="isBlocked ? 'danger' : 'warning'" class="mt-2 mb-2 py-2">
            <b-icon icon="exclamation-triangle-fill" />
            <template v-if="isBlocked">
                Enforcement is <strong>active</strong> for this plan. The drugs below (within their effective window)
                cannot be billed.
            </template>
            <template v-else>
                Enforcement is <strong>active</strong> for this plan. Only the drugs below (within their effective
                window) can be billed.
            </template>
        </b-alert>
        <b-alert v-else-if="planEnforceFlag" show variant="secondary" class="mt-2 mb-2 py-2">
            This plan opts in to the {{ isBlocked ? 'blocked' : 'approved' }} drug list, but the tenant-wide setting is
            off, so it is not currently enforced.
        </b-alert>

        <b-form-row class="align-items-end mt-2" v-if="!disabled">
            <b-col cols="5">
                <SearchComponent :id="drug && drug.id" v-model="drug" label="Drug" :search-u-r-l="`/Drug`"
                    :params-in-get-by-id="{ drugSource: drug && drug.source }" />
            </b-col>
            <b-col cols="3">
                <b-form-group label="Effective Date" class="mb-0">
                    <b-form-datepicker v-model="effectiveDate" reset-button today-button
                        placeholder="No start date" />
                </b-form-group>
            </b-col>
            <b-col cols="3">
                <b-form-group label="Expiration Date" class="mb-0">
                    <b-form-datepicker v-model="expirationDate" reset-button today-button
                        placeholder="Never expires" />
                </b-form-group>
            </b-col>
            <b-col cols="1">
                <n-button :type="isBlocked ? 'danger' : 'success'" :disabled="!canAdd" @click="addDrug">Add</n-button>
            </b-col>
        </b-form-row>

        <b-table class="mt-3" striped outlined :items="entries" :fields="fields" sort-icon-left responsive
            show-empty :empty-text="emptyText">
            <template #cell(effectiveDate)="row">
                {{ row.item.effectiveDate ? formatDay(row.item.effectiveDate) : '—' }}
            </template>
            <template #cell(expirationDate)="row">
                {{ row.item.expirationDate ? formatDay(row.item.expirationDate) : '—' }}
            </template>
            <template #cell(status)="row">
                <span :class="statusClass(row.item)">{{ statusText(row.item) }}</span>
            </template>
            <template #cell(actions)="row">
                <b-button v-if="!disabled" v-b-tooltip="{ trigger: 'hover', placement: 'left', boundary: 'window' }"
                    variant="link" size="sm"
                    class="p-0 text-danger text-decoration-none" :aria-label="`Remove drug from ${listNoun} list`"
                    :title="`Remove ${row.item.drugName || 'drug'} from ${listNoun} list`"
                    @click="confirmRemove(row.item)">
                    <b-icon icon="x-circle-fill" />
                </b-button>
            </template>
        </b-table>

        <b-modal v-model="showHistory" :title="`${title} History`" size="lg" hide-footer>
            <b-table v-if="history.length" striped small :items="historyRows" :fields="historyFields" responsive>
                <template #cell(updated)="row">{{ formatDateTime(row.item.updated) }}</template>
            </b-table>
            <p v-else class="text-muted mb-0">No history in the selected range.</p>
        </b-modal>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import moment from 'moment';

import SearchComponent from '@/components/SearchComponent.vue';
import HelpPopup from '@/components/HelpPopup.vue';
import { Drug } from '@/models/Drug/Drug';
import { Plan } from '@/models/Plan';
import { PlanDrugList, PlanDrugListHistory, PlanDrugListType } from '@/models/PlanDrugList';
import { PlanDrugListService } from '@/services/PlanDrugListService';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    name: 'PlanDrugList',
    components: { SearchComponent, HelpPopup },
})
export default class PlanDrugListComponent extends Vue {
    /** The plan being edited. The enforcement banner reads its per-plan opt-in flag. */
    @Prop({ required: true }) plan!: Plan;
    /** Which list this instance manages (Approved allow list or Blocked deny list). */
    @Prop({ required: true }) listType!: PlanDrugListType;
    /** Whether the tenant-wide switch for THIS list type is on (for the enforcement banner). */
    @Prop({ default: false }) tenantEnforceEnabled!: boolean;
    @Prop({ default: false }) disabled!: boolean;

    protected drug: Drug = new Drug();
    protected effectiveDate: string | null = null;
    protected expirationDate: string | null = null;
    protected entries: PlanDrugList[] = [];
    protected history: PlanDrugListHistory[] = [];
    protected showHistory = false;

    protected fields = [
        { key: 'drugName', label: 'Drug', sortable: true },
        { key: 'ndc', label: 'NDC', sortable: true },
        { key: 'effectiveDate', label: 'Effective', sortable: true },
        { key: 'expirationDate', label: 'Expires', sortable: true },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: '', tdClass: 'text-right', thClass: 'actions-col' },
    ];

    protected historyFields = [
        { key: 'updated', label: 'When' },
        { key: 'updatedBy', label: 'User' },
        { key: 'action', label: 'Action' },
    ];

    get isBlocked(): boolean {
        return this.listType === PlanDrugListType.Blocked;
    }

    get title(): string {
        return this.isBlocked ? 'Blocked Drug List' : 'Approved Drug List';
    }

    get listNoun(): string {
        return this.isBlocked ? 'blocked' : 'approved';
    }

    get emptyText(): string {
        return `No ${this.listNoun} drugs configured for this plan.`;
    }

    get planId(): number | null {
        return (this.plan?.id as number) || null;
    }

    get planEnforceFlag(): boolean {
        return this.isBlocked ? !!this.plan?.enforceBlockedDrugList : !!this.plan?.enforceApprovedDrugList;
    }

    get enforced(): boolean {
        return this.planEnforceFlag && !!this.tenantEnforceEnabled;
    }

    get canAdd(): boolean {
        // drug.id is the drugId (== PackageID) — the same identity the billing gate matches on.
        if (this.expirationDate && this.effectiveDate && this.expirationDate <= this.effectiveDate) return false;
        return !!(this.drug && this.drug.id);
    }

    @Watch('planId', { immediate: true })
    onPlanIdChanged(newId: number | null) {
        if (newId) {
            this.loadEntries();
        } else {
            this.entries = [];
        }
    }

    async loadEntries() {
        if (!this.planId) return;
        try {
            this.entries = await PlanDrugListService.getByPlan(this.planId, this.listType);
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        }
    }

    async addDrug() {
        if (!this.planId || !this.canAdd) return;
        try {
            await PlanDrugListService.add(this.planId, this.listType, {
                drugSource: this.drug.source,
                packageId: Number(this.drug.id),
                drugName: this.drug.productNameShort || this.drug.productNameLong,
                ndc: this.drug.ndc,
                effectiveDate: this.effectiveDate,
                expirationDate: this.expirationDate,
            });
            this.$notification(NotificationOptions.successSaveNotificationPreset(`${this.isBlocked ? 'Blocked' : 'Approved'} drug`));
            this.drug = new Drug();
            this.effectiveDate = null;
            this.expirationDate = null;
            await this.loadEntries();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        }
    }

    confirmRemove(item: PlanDrugList) {
        const label = item.drugName || item.ndc || 'this drug';
        this.$bvModal.msgBoxConfirm(`Remove ${label} from this plan's ${this.listNoun} drug list?`, {
            title: 'Confirm Remove',
            okVariant: 'danger',
            okTitle: 'Remove',
            cancelTitle: 'Cancel',
            centered: true,
        }).then(async ok => {
            if (!ok) return;
            try {
                await PlanDrugListService.remove(item.id);
                await this.loadEntries();
            } catch (error) {
                this.$notification(NotificationOptions.error(error));
            }
        }).catch(() => { /* dismissed */ });
    }

    async openHistory() {
        if (!this.planId) return;
        try {
            const to = moment().endOf('day').toDate();
            const from = moment().subtract(2, 'years').startOf('day').toDate();
            this.history = await PlanDrugListService.getHistory(this.planId, this.listType, from, to);
            this.showHistory = true;
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        }
    }

    get historyRows() {
        return this.history.map(h => ({
            updated: h.updated,
            updatedBy: h.updatedBy,
            // Action is derived server-side (see usp_PlanDrugList_GetHistoryByPlanId).
            action: h.action ?? 'Added / Updated',
        }));
    }

    statusText(item: PlanDrugList): string {
        if (item.effectiveDate && moment().isBefore(moment(item.effectiveDate))) return 'Scheduled';
        if (item.expirationDate && !moment().isBefore(moment(item.expirationDate))) return 'Expired';
        return this.isBlocked ? 'Blocking' : 'Active';
    }

    // Text-only coloring for the status indicator (no row-variant reuse).
    statusClass(item: PlanDrugList): string {
        const status = this.statusText(item);
        if (status === 'Scheduled') return 'text-info';
        if (status === 'Expired') return 'text-muted';
        // Currently in effect: green for an approved (billable) drug, red for a blocked one.
        return this.isBlocked ? 'text-danger font-weight-bold' : 'text-success font-weight-bold';
    }

    formatDay(value: string): string {
        return moment(value).format('MM-DD-YYYY');
    }

    formatDateTime(value: Date | string): string {
        return moment(value).format('MM-DD-YYYY, h:mm a');
    }
}
</script>

<style scoped>
:deep(.actions-col) {
    width: 48px;
}
</style>
